<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import AssignmentPolicyForm from 'dashboard/routes/dashboard/settings/assignmentPolicy/pages/components/AgentAssignmentPolicyForm.vue';

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const formRef = ref(null);
const uiFlags = useMapGetter('assignmentPolicies/getUIFlags');

const goBack = () => {
  router.push({ name: 'agent_assignment_policy_index' });
};

const handleSubmit = async formState => {
  try {
    const policy = await store.dispatch('assignmentPolicies/create', formState);
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.API.SUCCESS_MESSAGE')
    );
    formRef.value?.resetForm();

    router.push({
      name: 'agent_assignment_policy_edit',
      params: { id: policy.id },
    });
  } catch (error) {
    useAlert(
      t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="policy-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <div class="flex items-center justify-between w-full">
        <button
          class="flex items-center gap-2 text-sm mb-3 transition-colors"
          style="color: #6b7280"
          @click="goBack"
        >
          <span class="i-ph-arrow-left-bold size-4" />
          {{
            $t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.TITLE')
          }}
        </button>
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
            style="
              background: linear-gradient(to bottom right, #f59e0b, #0058d8);
              box-shadow: 0 10px 15px -3px rgba(245, 158, 11, 0.25);
            "
          >
            <span class="i-ph-plus-bold text-white size-5" />
          </div>
          <h1 class="text-xl font-semibold" style="color: #111827">
            {{
              $t(
                'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.HEADER.TITLE'
              )
            }}
          </h1>
        </div>
      </div>
    </header>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto p-6">
      <div class="form-container max-w-4xl">
        <AssignmentPolicyForm
          ref="formRef"
          mode="CREATE"
          :is-loading="uiFlags.isCreating"
          @submit="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.policy-page,
.dark .policy-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc-bar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-content {
  background: #ffffff;
}
.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}
.ws-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ws-btn:hover {
  background: #1f2937;
}
.ws-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.ws-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
.ws-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: ws-spin 0.9s linear infinite;
}
@keyframes ws-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Assignment policy component-level light theme */
.policy-page {
  --wlab-bg-tertiary: #f9fafb;
  --wlab-bg-hover: #fff7ed;
  --wlab-border-default: #e5e7eb;
  --wlab-text-muted: #9ca3af;
  --wlab-text-secondary: #0066ff;
  --wlab-shadow-card: 0 1px 3px rgba(15, 23, 42, 0.08),
    0 1px 2px rgba(15, 23, 42, 0.05);
  --wlab-shadow-card-hover: 0 14px 24px -16px rgba(15, 23, 42, 0.28);
}

:deep(.wlab-card-modern) {
  background: #ffffff !important;
  outline-color: #e5e7eb !important;
}

:deep(.wlab-card-modern:hover) {
  outline-color: #0066ff !important;
}

:deep(.radio-card) {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}

:deep(.radio-card--active) {
  background: #fff7ed !important;
  border-color: #0066ff !important;
}

:deep(.radio-input) {
  accent-color: #0066ff !important;
}

:deep(.outline-n-container) {
  outline-color: #e5e7eb !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-solid-3),
:deep(.bg-n-background) {
  background: #ffffff !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}
:deep(.bg-n-alpha-1),
:deep(.bg-n-alpha-2) {
  background: #f9fafb !important;
}

:deep(input:not(.reset-base)),
:deep(select:not(.reset-base)),
:deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
</style>
